import { motion, AnimatePresence } from "motion/react";
import { X, Check, Plus, Pencil, Search, ChevronDown, ZoomIn, ZoomOut, Info } from "lucide-react";
import { useState, useRef, useEffect, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import type { CardSize } from "../../components/ConfigurableCardGrid";

/* Minimal metadata the panel needs to list a card. */
export interface PanelCard {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: LucideIcon;
  about?: string;     // longer "what & why" shown on the preview's flip side
  size?: CardSize;    // footprint → preview renders at the card's real proportions
}

/* Preview zoom bounds. */
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.25;

/* Right-hand slide-over that lists every catalog card. Toggling a card
   adds/removes it from the live layout. The "Edit layout" action hands off to
   drag-and-drop mode (the panel closes).

   When `renderPreview` is supplied, each card gets an expand chevron that opens
   a live preview of the card with zoom in/out controls. */
export function AddCardsPanel({
  open, active, cards, categories, onToggle, onClose, onEdit, renderPreview, initialExpandedId,
}: {
  open: boolean;
  active: string[];               // ids currently on the dashboard
  cards: PanelCard[];             // full catalog to choose from
  categories: readonly string[];  // section order
  onToggle: (id: string) => void;
  onClose: () => void;
  onEdit: () => void;
  renderPreview?: (id: string) => ReactNode; // live card preview (optional)
  initialExpandedId?: string;     // open with this card's preview expanded
}) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [flipped, setFlipped] = useState<Set<string>>(new Set());
  const [zoom, setZoom] = useState<Record<string, number>>({});
  const activeSet = new Set(active);
  const q = search.trim().toLowerCase();

  const listRef = useRef<HTMLDivElement>(null);
  /* When opened from a "View detail" action, expand that card's preview and
     scroll it into view. */
  useEffect(() => {
    if (!open || !initialExpandedId) return;
    setExpanded(new Set([initialExpandedId]));
    const t = setTimeout(() => {
      listRef.current
        ?.querySelector(`[data-card="${initialExpandedId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 220);
    return () => clearTimeout(t);
  }, [open, initialExpandedId]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleFlip = (id: string) =>
    setFlipped((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const zoomFor = (id: string) => zoom[id] ?? 1;
  const adjustZoom = (id: string, delta: number) =>
    setZoom((prev) => ({
      ...prev,
      [id]: Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, (prev[id] ?? 1) + delta)),
    }));

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* scrim */}
          <motion.div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          {/* panel */}
          <motion.aside
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[540px] flex-col border-l border-border bg-card shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 380, damping: 38 }}
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-border p-4">
              <div>
                <h2 className="text-base font-bold text-foreground">Add cards</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  KPI cards and charts for your dashboard
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* edit handoff */}
            <div className="border-b border-border p-4">
              <button
                type="button"
                onClick={onEdit}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
              >
                <Pencil className="size-4" />
                Edit layout
              </button>
              <p className="mt-2 text-center text-[11px] text-muted-foreground">
                Rearrange and resize cards by dragging
              </p>
            </div>

            {/* search */}
            <div className="border-b border-border p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search cards"
                  className="h-10 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-indigo-500/50"
                />
              </div>
            </div>

            {/* catalog list */}
            <div ref={listRef} className="flex-1 overflow-y-auto p-4">
              <div className="flex flex-col gap-6">
                {categories.map((category) => {
                  const inCategory = cards.filter(
                    (c) =>
                      c.category === category &&
                      (!q ||
                        c.title.toLowerCase().includes(q) ||
                        c.description.toLowerCase().includes(q)),
                  );
                  if (inCategory.length === 0) return null;
                  return (
                    <section key={category}>
                      <h3 className="mb-2.5 text-[11px] font-bold tracking-wider text-foreground/60">
                        {category}
                      </h3>
                      <div className="flex flex-col gap-2">
                        {inCategory.map((card) => {
                          const on = activeSet.has(card.id);
                          const isExpanded = expanded.has(card.id);
                          const isFlipped = flipped.has(card.id);
                          const z = zoomFor(card.id);
                          const Icon = card.icon;
                          return (
                            <div
                              key={card.id}
                              data-card={card.id}
                              className={`rounded-lg border transition-all ${
                                on
                                  ? "border-indigo-500/40 bg-indigo-500/[0.05]"
                                  : "border-border bg-background hover:border-foreground/20"
                              }`}
                            >
                              <div className="flex items-start gap-3 p-3">
                                <button
                                  type="button"
                                  onClick={() => onToggle(card.id)}
                                  className="flex min-w-0 flex-1 items-start gap-3 text-left"
                                >
                                  <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-foreground/[0.06]">
                                    <Icon className="size-4 text-foreground/70" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="block text-sm font-semibold text-foreground">{card.title}</span>
                                    <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                                      {card.description}
                                    </span>
                                  </div>
                                </button>

                                {/* expand / collapse preview */}
                                {renderPreview && (
                                  <button
                                    type="button"
                                    onClick={() => toggleExpand(card.id)}
                                    title={isExpanded ? "Hide preview" : "Show preview"}
                                    className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-indigo-400/50 hover:text-foreground"
                                  >
                                    <ChevronDown
                                      className={`size-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                    />
                                  </button>
                                )}

                                {/* add / remove indicator */}
                                <button
                                  type="button"
                                  onClick={() => onToggle(card.id)}
                                  title={on ? "Remove from dashboard" : "Add to dashboard"}
                                  className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md border transition-colors ${
                                    on
                                      ? "border-indigo-500 bg-indigo-500 text-white"
                                      : "border-border text-muted-foreground hover:border-indigo-400/50"
                                  }`}
                                >
                                  {on ? <Check className="size-3.5" /> : <Plus className="size-3.5" />}
                                </button>
                              </div>

                              {/* live preview with zoom + flip-to-about */}
                              {isExpanded && renderPreview && (
                                <div className="border-t border-border p-3">
                                  <div className="mb-2 flex items-center justify-between">
                                    <span className="text-[10px] font-semibold tracking-wider text-muted-foreground">
                                      {isFlipped ? "About this card" : "Preview"}
                                    </span>
                                    <div className="flex items-center gap-1">
                                      {!isFlipped && (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => adjustZoom(card.id, -ZOOM_STEP)}
                                            disabled={z <= ZOOM_MIN}
                                            title="Zoom out"
                                            className="flex size-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                                          >
                                            <ZoomOut className="size-4" />
                                          </button>
                                          <span className="w-11 text-center text-[11px] font-semibold tabular-nums text-muted-foreground">
                                            {Math.round(z * 100)}%
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => adjustZoom(card.id, ZOOM_STEP)}
                                            disabled={z >= ZOOM_MAX}
                                            title="Zoom in"
                                            className="flex size-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                                          >
                                            <ZoomIn className="size-4" />
                                          </button>
                                          <span className="mx-0.5 h-5 w-px bg-border" />
                                        </>
                                      )}
                                      {/* (i) — flip to the description and back */}
                                      <button
                                        type="button"
                                        onClick={() => toggleFlip(card.id)}
                                        title={isFlipped ? "Show preview" : "About this card"}
                                        className={`flex size-7 items-center justify-center rounded-md border transition-colors ${
                                          isFlipped
                                            ? "border-indigo-500 bg-indigo-500 text-white shadow-sm shadow-indigo-500/40"
                                            : "border-indigo-500/50 bg-indigo-500/10 text-indigo-500 hover:bg-indigo-500/20 dark:text-indigo-400"
                                        }`}
                                      >
                                        <Info className="size-4" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* flip container: front = live preview, back = about */}
                                  <div style={{ perspective: 1400 }}>
                                    <motion.div
                                      className="relative"
                                      style={{ transformStyle: "preserve-3d" }}
                                      animate={{ rotateY: isFlipped ? 180 : 0 }}
                                      transition={{ duration: 0.5, ease: "easeInOut" }}
                                    >
                                      {/* front */}
                                      <div style={{ backfaceVisibility: "hidden" }}>
                                        <CardPreview
                                          zoom={z}
                                          onZoomDelta={(d) => adjustZoom(card.id, d)}
                                          size={card.size ?? "md"}
                                        >
                                          {renderPreview(card.id)}
                                        </CardPreview>
                                      </div>
                                      {/* back */}
                                      <div
                                        className="absolute inset-0"
                                        style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                                      >
                                        <AboutFace card={card} />
                                      </div>
                                    </motion.div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

/* Back of the flip: explains what the card is and why it's useful. */
export function AboutFace({ card }: { card: PanelCard }) {
  const Icon = card.icon;
  return (
    <div className="flex h-full flex-col justify-center gap-3 overflow-auto rounded-lg border border-indigo-500/30 bg-gradient-to-br from-indigo-500/[0.08] to-card p-4">
      <div className="flex items-center gap-2.5">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-500/15">
          <Icon className="size-5 text-indigo-500 dark:text-indigo-400" />
        </div>
        <div className="min-w-0">
          <h4 className="truncate text-sm font-bold text-foreground">{card.title}</h4>
          <p className="text-[11px] text-muted-foreground">{card.description}</p>
        </div>
      </div>
      <p className="text-xs leading-relaxed text-foreground/80">
        {card.about ?? card.description}
      </p>
    </div>
  );
}

/* Live card preview that fits the FULL card into the panel width at 100% zoom
   (no scroll), then scales up/down with the zoom control. Each card is rendered
   at a desktop "design" size matching its real footprint — so a wide table shows
   every column (no internal scrollbar) and a KPI keeps its small proportions —
   then the whole thing is scaled to the measured panel width. */
const PREVIEW_BASE: Record<CardSize, { w: number; h: number }> = {
  sm:   { w: 820, h: 460 },
  md:   { w: 820, h: 460 },
  wide: { w: 820, h: 460 },
  full: { w: 1120, h: 460 }, // wide enough for the full table (every column fits, no scrollbar)
};

function CardPreview({
  zoom, onZoomDelta, size, children,
}: {
  zoom: number;
  onZoomDelta: (delta: number) => void;
  size: CardSize;
  children: ReactNode;
}) {
  const base = PREVIEW_BASE[size] ?? PREVIEW_BASE.md;
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxW, setBoxW] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ sx: number; sy: number; bx: number; by: number } | null>(null);

  /* measure the box width so the card fits exactly at 100% */
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const update = () => setBoxW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = boxW ? boxW / base.w : 0.5; // scale that makes the card fill the width
  const fittedH = base.h * fit;

  /* How far the (centre-scaled) content can be panned before its edge reaches
     the box edge. Zero when not zoomed in → nothing to pan. */
  const maxPanX = Math.max(0, (boxW * (zoom - 1)) / 2);
  const maxPanY = Math.max(0, (fittedH * (zoom - 1)) / 2);
  const clampPan = (p: { x: number; y: number }) => ({
    x: Math.min(maxPanX, Math.max(-maxPanX, p.x)),
    y: Math.min(maxPanY, Math.max(-maxPanY, p.y)),
  });

  /* re-clamp / recentre the pan whenever the zoom (or width) changes */
  useEffect(() => {
    setPan((p) => ({
      x: Math.min(maxPanX, Math.max(-maxPanX, p.x)),
      y: Math.min(maxPanY, Math.max(-maxPanY, p.y)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, boxW]);

  /* scroll up = zoom in, scroll down = zoom out (native non-passive listener so
     we can prevent the panel from scrolling while zooming) */
  const deltaRef = useRef(onZoomDelta);
  deltaRef.current = onZoomDelta;
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      deltaRef.current(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const pannable = zoom > 1;

  const onPointerDown = (e: React.PointerEvent) => {
    if (!pannable) return; // nothing to drag at fit
    dragRef.current = { sx: e.clientX, sy: e.clientY, bx: pan.x, by: pan.y };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setPan(clampPan({
      x: dragRef.current.bx + (e.clientX - dragRef.current.sx),
      y: dragRef.current.by + (e.clientY - dragRef.current.sy),
    }));
  };
  const endDrag = () => { dragRef.current = null; setDragging(false); };

  return (
    <div
      ref={boxRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      title={pannable ? "Drag to move · scroll to zoom" : "Scroll to zoom in"}
      className={`overflow-hidden rounded-lg ${
        dragging ? "cursor-grabbing" : pannable ? "cursor-grab" : "cursor-zoom-in"
      }`}
      style={{ height: fittedH, touchAction: "none" }}
    >
      {/* pan + zoom layer: scales/pans the fitted card from its centre */}
      <div
        className="h-full w-full"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: "center center",
        }}
      >
        {/* fit layer: renders the full card scaled to the box; non-interactive
            so the whole area is the pan/zoom surface */}
        <div
          className="pointer-events-none"
          style={{
            width: base.w,
            height: base.h,
            transform: `scale(${fit})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
